import React, { type FC } from "react"
import { Link } from "react-router-dom"
import { type EntityCategory, TransactionRecordType } from "@digits-graphql/frontend/graphql-bearer"
import { LoadingBlock } from "@digits-shared/components/Loaders"
import { svgIconStyles, svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgArrowUpRight } from "@digits-shared/components/SVGIcons/line/ArrowUpRight.svg"
import { SvgChevronDownSolid } from "@digits-shared/components/SVGIcons/solid/ChevronDownSolid.svg"
import {
  IconSize,
  IconSVGContainerNoBackgroundColor,
} from "@digits-shared/components/UI/Icons/Icon"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { CATEGORY_COLOR_MAP } from "src/frontend/components/OS/Applications/Accounting/TransactionDashboard/TransactionUI"
import { CategoryHierarchyCrumbs } from "src/frontend/components/Shared/Transactions/Categories/CategoryHierarchyCrumbs"
import { CategorySummary } from "src/frontend/components/Shared/Transactions/Categories/CategorySummary"
import {
  type ExportOptions,
  type TableCollapsingState,
} from "src/frontend/components/Shared/Transactions/Categories/categoryTableTypes"
import { CategoryIcon } from "src/frontend/components/Shared/Transactions/Icons/CategoryIcon"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session/FrontendSession"
import { type HierarchicalDimensionSummaryNode } from "src/shared/helpers/hierarchicalDimensionHelper"
import { useStopClickPropagation } from "src/shared/hooks/useStopClickPropagation"

/*
  STYLES
*/

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  &.collapsing {
    cursor: ns-resize;
    &:hover {
      .categoryIcon {
        display: none;
      }

      .expandChevron {
        display: flex;
      }
    }
  }
`

const HeaderGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
`

const LinkIcon = styled(SvgArrowUpRight)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.secondary50, 1.5)};
  transition: opacity 0.2s ease-in-out;
  opacity: 0;
`

const CategoryNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  width: fit-content;

  &:hover {
    background: ${colors.secondary05};
    ${LinkIcon} {
      opacity: 1;
      ${svgPathStyles(colors.secondary, 1.5)};
    }
  }
`

const IconContainer = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CategoryHeaderText = styled.div`
  font-size: 14px;
  font-weight: ${fonts.weight.heavy};
  color: ${colors.secondary};
`

const CategoryHeaderSubText = styled.div`
  font-size: 14px;
  color: ${colors.secondary80};
`

const Hierarchy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TransactionCategoryIconStyled = styled(CategoryIcon)<{ color?: string }>`
  display: inline-flex;

  ${IconSVGContainerNoBackgroundColor} {
    border-radius: 5px;
    background: ${({ color }) => color || colors.gray};
  }
`

const ChevronIcon = styled(SvgChevronDownSolid)`
  width: 16px;
  height: 16px;
  ${svgIconStyles(colors.secondary)};
  transition: transform 0.2s ease-in-out;
  transform: rotate(0);
  .table-collapsed & {
    transform: rotate(-90deg);
  }
`

const ChevronContainer = styled.div`
  width: 24px;
  height: 24px;
  display: none;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${colors.secondary05};
  cursor: pointer;
`

/*
  INTERFACES
*/

interface Props {
  categorySummary?: HierarchicalDimensionSummaryNode<EntityCategory>
  loading?: boolean
  collapsing?: TableCollapsingState
  exportOptions?: ExportOptions
}

interface HeaderProps {
  categorySummary: HierarchicalDimensionSummaryNode<EntityCategory>
}

/*
  COMPONENTS
*/

export const CategoryTableHeader: FC<Props> = ({
  categorySummary,
  loading,
  collapsing,
  exportOptions,
}) => {
  if (!categorySummary && loading) return <LoadingTableHeader />
  if (!categorySummary) return <NoDataHeader />

  const { dimension: category } = categorySummary

  return (
    <HeaderContainer
      id={category.id}
      className={stringHelper.classNames(collapsing && "collapsing")}
      onClick={collapsing?.toggle}
    >
      <HeaderGroup>
        <CategoryHeader categorySummary={categorySummary} />
      </HeaderGroup>
      <HeaderGroup>
        <CategorySummary categorySummary={categorySummary} exportOptions={exportOptions} />
      </HeaderGroup>
    </HeaderContainer>
  )
}

const CategoryHeader: React.FC<HeaderProps> = ({ categorySummary }) => {
  const { dimension: category } = categorySummary
  const generatePath = useFrontendPathGenerator()
  const stopPropagation = useStopClickPropagation()
  const { currentLegalEntity } = useSession<FrontendSession>()
  const leSlug = currentLegalEntity?.slug

  const path = generatePath(
    routes.categoryDetails,
    { categoryId: categorySummary.dimension.id, leSlug },
    "includeAllQueryParams"
  )

  return (
    <>
      <IconContainer>
        <ChevronContainer className="expandChevron">
          <ChevronIcon />
        </ChevronContainer>

        <TransactionCategoryIconStyled
          category={category}
          className="categoryIcon"
          color={CATEGORY_COLOR_MAP.get(category.type)}
          recordType={TransactionRecordType.Transaction}
          iconSize={IconSize.MediumPlus}
        />
      </IconContainer>

      <Hierarchy>
        <CategoryHierarchyCrumbs categorySummary={categorySummary} />
        <Link to={path} onClick={stopPropagation}>
          <CategoryNameContainer>
            <CategoryHeaderText>{category.name}</CategoryHeaderText>
            {category.chartOfAccountsDisplayNumber && (
              <CategoryHeaderSubText>{category.chartOfAccountsDisplayNumber}</CategoryHeaderSubText>
            )}
            <LinkIcon className="categoryLinkIcon" />
          </CategoryNameContainer>
        </Link>
      </Hierarchy>
    </>
  )
}

const LoadingTableHeader: React.FC = () => (
  <HeaderContainer>
    <HeaderGroup>
      <IconContainer>
        <LoadingBlock height={32} width={32} />
      </IconContainer>

      <div>
        <div>
          <LoadingBlock height={16} width={120} />
        </div>
        <LoadingBlock height={15} width={80} />
      </div>
    </HeaderGroup>
    <HeaderGroup>
      <LoadingBlock height={16} width={110} />
    </HeaderGroup>
  </HeaderContainer>
)

const NoDataHeader: React.FC = () => (
  <HeaderContainer>
    <HeaderGroup>
      <CategoryHeaderText css="padding: 10px 0">No Transactions</CategoryHeaderText>
    </HeaderGroup>
    <HeaderGroup />
  </HeaderContainer>
)
